/**
 * @file 練習ログ・学びメモの添付画像のアップロード・削除 (Firebase Storage 直 write)。
 * @what: 選ばれた画像を canvas で長辺 1600px の JPEG に再エンコードして
 *   families/{familyId}/study_log_images/{logId}/{uuid}.jpg (練習ログ) または
 *   families/{familyId}/study_note_images/{noteId}/{uuid}.jpg (学びメモ) へ上げ、パスを返す。
 * @why: canvas を通すのはサイズ圧縮と同時に EXIF (GPS 含む) を確実に落とすため
 *   (写真アップロード経路の GPS 除去必須の方針)。書き込みは storage.rules の
 *   study_log_images 限定の member write (HC-LOGIMG-1-R のデプロイが前提)。
 *   平文 JPEG は study 層 posture B の線引き (教材・成果物の写真) によるもので、
 *   将来の E2E 暗号化は image_enc フィールド側で表現する。
 * @failure: 変換・アップロードの失敗は throw (呼び出し側が文言表示)。削除は best-effort
 *   (失敗しても投げない。記録の削除を孤児画像で止めない)。
 */

import { deleteObject, getBytes, ref as storageRef, uploadBytes } from "firebase/storage";
import { HANDWRITING_ID_PREFIX, handwritingStrokesPath } from "../model/handwriting";
import { getFirebaseStorage } from "@/shared/firebase/client";

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

async function toJpeg(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("image decode failed"));
      img.src = url;
    });
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    // 高さは丸めた幅からの比で出す (幅・高さを別々に丸めて比が僅かにずれるのを避ける)
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * (canvas.width / img.naturalWidth)));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d unavailable");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
    });
    if (!blob) throw new Error("jpeg encode failed");
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** ファイル名用の乱数 id。crypto.randomUUID の無い古いブラウザ (iOS 15.3 以前) は
 *  時刻 + 乱数で代用する (衝突耐性はログ id 配下のファイル名として十分)。 */
function randomFileId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 画像を再エンコードして上げ、Storage パスを返す。 */
export async function uploadStudyLogImage(file: File, familyId: string, logId: string): Promise<string> {
  const blob = await toJpeg(file);
  const path = `families/${familyId}/study_log_images/${logId}/${randomFileId()}.jpg`;
  await uploadBytes(storageRef(getFirebaseStorage(), path), blob, { contentType: "image/jpeg" });
  return path;
}

/**
 * 学びメモの画像を再エンコードして上げ、参照キーと Storage パスを返す。
 *
 * @why: ファイル名 = 参照キー (id)。本文の `note-image:<id>` と Storage の実体、
 *   ノートの images メタが 1 つの id で結ばれ、CLI (note-images) が突き合わせられる。
 */
export async function uploadStudyNoteImage(
  file: File,
  familyId: string,
  noteId: string,
): Promise<{ id: string; path: string }> {
  const blob = await toJpeg(file);
  const id = randomFileId().toLowerCase();
  const path = `families/${familyId}/study_note_images/${noteId}/${id}.jpg`;
  await uploadBytes(storageRef(getFirebaseStorage(), path), blob, { contentType: "image/jpeg" });
  return { id, path };
}

/** 記録の削除に伴う画像の後始末 (best-effort)。 */
export function deleteStudyLogImage(path: string): void {
  try {
    deleteObject(storageRef(getFirebaseStorage(), path)).catch((err: unknown) => {
      console.warn("[study] failed to delete log image", err);
    });
  } catch (err) {
    console.warn("[study] failed to delete log image", err);
  }
}

/** ノートの削除に伴う画像の後始末 (best-effort)。 */
export function deleteStudyNoteImage(path: string): void {
  try {
    deleteObject(storageRef(getFirebaseStorage(), path)).catch((err: unknown) => {
      console.warn("[study] failed to delete note image", err);
    });
  } catch (err) {
    console.warn("[study] failed to delete note image", err);
  }
  // 手書きは線の JSON も隣に持つので、一緒に片づける (best-effort)。
  if (path.includes(`/${HANDWRITING_ID_PREFIX}`) && path.endsWith(".png")) {
    try {
      deleteObject(storageRef(getFirebaseStorage(), handwritingStrokesPath(path))).catch(
        (err: unknown) => {
          console.warn("[study] failed to delete handwriting strokes", err);
        },
      );
    } catch (err) {
      console.warn("[study] failed to delete handwriting strokes", err);
    }
  }
}

/**
 * 手書きを PNG と線の JSON の2つで上げ、参照キーと Storage パスを返す。
 *
 * @what: id は `hw-` で始め、`<id>.png` の隣に `<id>.strokes.json` を置く (contentType は
 *   storage.rules が許す application/octet-stream)。本文の参照と images メタは PNG だけを指す。
 * @why: 画像だけだと書き足せない。線の座標を残しておけば、あとから開いて続きが書ける。
 * @failure: どちらかの送信に失敗したら throw (呼び出し側が文言表示)。PNG だけ上がって JSON が
 *   無いときは、書き足しはできないが画像としては使える。
 */
export async function uploadStudyNoteDrawing(
  png: Blob,
  strokesJson: string,
  familyId: string,
  noteId: string,
): Promise<{ id: string; path: string }> {
  const id = `${HANDWRITING_ID_PREFIX}${randomFileId().toLowerCase()}`;
  const path = `families/${familyId}/study_note_images/${noteId}/${id}.png`;
  const storage = getFirebaseStorage();
  await uploadBytes(storageRef(storage, path), png, { contentType: "image/png" });
  await uploadBytes(
    storageRef(storage, handwritingStrokesPath(path)),
    new Blob([strokesJson]),
    { contentType: "application/octet-stream" },
  );
  return { id, path };
}

/** 手書きの線の JSON を読む (文字列のまま返す。形の検証は呼び出し側)。 */
export async function fetchStudyNoteDrawing(imagePath: string): Promise<string> {
  const bytes = await getBytes(storageRef(getFirebaseStorage(), handwritingStrokesPath(imagePath)));
  return new TextDecoder().decode(bytes);
}

/** 既にある手書きを同じパスに上書きする (参照キーは変わらない)。 */
export async function saveStudyNoteDrawing(
  png: Blob,
  strokesJson: string,
  imagePath: string,
): Promise<void> {
  const storage = getFirebaseStorage();
  await uploadBytes(storageRef(storage, imagePath), png, { contentType: "image/png" });
  await uploadBytes(
    storageRef(storage, handwritingStrokesPath(imagePath)),
    new Blob([strokesJson]),
    { contentType: "application/octet-stream" },
  );
}
